import { useEffect, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { cleanPhone, timeAgo } from "../lib/utils";
import { Badge, Btn, Card, Icon, Input } from "../components";

export default function BroadcastPage({ token, chats, broadcasts, setBroadcasts, socket, showToast, plan, setPage, onLimitHit }) {

  const [view,     setView]     = useState("list");
  const [name,     setName]     = useState("");
  const [message,  setMessage]  = useState("");
  const [selected, setSelected] = useState([]);
  const [progress, setProgress] = useState({});
  const [creating, setCreating] = useState(false);
  const [search,   setSearch]   = useState("");

  useEffect(() => {
    if (!socket) return;
    socket.on("broadcast_progress", d => setProgress(p => ({ ...p, [d.broadcastId]: d })));
    socket.on("broadcast_complete",  d => {
      setBroadcasts(p => p.map(b => b._id === d.broadcastId
        ? { ...b, status: "completed", sentCount: d.sent, failedCount: d.failed }
        : b
      ));
      setProgress(p => { const n = { ...p }; delete n[d.broadcastId]; return n; });
      showToast?.(`Broadcast done — ${d.sent} sent, ${d.failed} failed`);
    });
    return () => { socket.off("broadcast_progress"); socket.off("broadcast_complete"); };
  }, [socket]);

  const toggleContact = (c) =>
    setSelected(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);

  const createAndSend = async () => {
    if (!message.trim()) return showToast?.("Write a message first", "error");
    if (!selected.length) return showToast?.("Select at least one contact", "error");
    setCreating(true);
    try {
      // Check broadcast limit before creating
      const limitCheck = await apiFetch("/api/check-limit?type=broadcasts", {}, token).catch(() => null);
      if (limitCheck?.reached) { onLimitHit?.("broadcasts"); setCreating(false); return; }

      const contacts = selected.map(c => ({
        contact:     c,
        contactName: chats.find(ch => ch.contact === c)?.contactName || cleanPhone(c),
      }));
      const data = await apiFetch("/api/broadcasts", {
        method: "POST",
        body:   JSON.stringify({ name: name.trim() || undefined, message, contacts }),
      }, token);
      if (data.broadcastId) {
        await apiFetch(`/api/broadcasts/${data.broadcastId}/send`, { method: "POST" }, token);
        const updated = await apiFetch("/api/broadcasts", {}, token);
        setBroadcasts(updated.broadcasts || []);
        setView("list"); setName(""); setMessage(""); setSelected([]);
        showToast?.("Broadcast started — sending in progress");
      } else if (data.limitReached) {
        onLimitHit?.("broadcasts");
      } else {
        showToast?.(data.error || "Failed to create broadcast", "error");
      }
    } catch { showToast?.("Network error", "error"); }
    setCreating(false);
  };

  const filteredChats = chats.filter(c =>
    (c.contactName || cleanPhone(c.contact)).toLowerCase().includes(search.toLowerCase()) ||
    cleanPhone(c.contact).includes(search)
  );

  // ── Pro gate (after all hooks) ────────────────────────────────────
  if (plan === "free") return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ maxWidth: 440, textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: 20, margin: "0 auto 24px",
          background: "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.10))",
          border: "1px solid rgba(99,102,241,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="1.8">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.text1, marginBottom: 10, letterSpacing: "-0.02em" }}>
          Broadcast is a Pro Feature
        </div>
        <div style={{ fontSize: 14, color: T.text3, lineHeight: 1.6, marginBottom: 28 }}>
          Upgrade to <strong style={{ color: T.text2 }}>Starter</strong> or higher to send broadcast campaigns to multiple contacts at once.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {[
            "Send one message to hundreds of contacts",
            "Track delivery & progress in real-time",
            "Schedule campaigns for later",
            "Personalized message variables",
          ].map(f => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: T.bg2, border: `1px solid ${T.border1}`, textAlign: "left" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span style={{ fontSize: 13, color: T.text2 }}>{f}</span>
            </div>
          ))}
        </div>
        <button onClick={() => setPage?.("plan")} style={{
          width: "100%", padding: "13px 24px", borderRadius: 12, border: "none", cursor: "pointer",
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff",
          fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em",
          boxShadow: "0 4px 20px rgba(99,102,241,0.35)", transition: "all 0.18s",
        }}>
          Upgrade Plan — From $29/mo
        </button>
        <div style={{ fontSize: 12, color: T.text3, marginTop: 12 }}>No contracts · Cancel anytime</div>
      </div>
    </div>
  );

  // ── New broadcast form ────────────────────────────────────────────
  if (view === "new") return (
    <div style={{ flex: 1, overflowY: "auto", padding: "clamp(14px, 4vw, 26px) clamp(14px, 4vw, 28px) 40px" }}>
      {/* Back */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <Btn variant="ghost" size="sm" onClick={() => setView("list")} style={{ border: `1px solid ${T.border2}` }}>
          <Icon name="arrowLeft" size={13} color={T.text2} /> Back
        </Btn>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text1 }}>New Broadcast Campaign</div>
          <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Send one message to many contacts at once</div>
        </div>
      </div>

      <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Message composer */}
        <div>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 18 }}>Campaign Details</div>
            <Input label="Campaign name (optional)" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Eid Offer, New Product Launch" />
            <div>
              <label style={{ display: "block", fontSize: 11, color: T.text3, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Message *</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Write your broadcast message here..."
                style={{ width: "100%", minHeight: 180, padding: "11px 14px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10, color: T.text1, fontSize: 13, outline: "none", resize: "vertical", fontFamily: base.font, lineHeight: 1.65, boxSizing: "border-box", transition: "border-color 0.2s" }}
                onFocus={e => (e.target.style.borderColor = T.emerald)}
                onBlur={e  => (e.target.style.borderColor = T.border1)} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.text3, marginTop: 6 }}>
                <span>{message.length} characters</span>
                <span>{selected.length} recipient{selected.length !== 1 ? "s" : ""} selected</span>
              </div>
            </div>
          </Card>

          {/* Preview */}
          {message && (
            <Card style={{ background: T.emeraldDim, border: `1px solid rgba(99,102,241,0.2)` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.emerald, marginBottom: 10 }}>Message Preview</div>
              <div style={{ padding: "10px 13px", borderRadius: 10, background: "rgba(99,102,241,0.1)", fontSize: 13, color: T.text1, lineHeight: 1.65, border: `1px solid rgba(99,102,241,0.15)` }}>
                {message}
              </div>
            </Card>
          )}

          <Btn variant="primary" size="lg" onClick={createAndSend} disabled={creating || !message.trim() || !selected.length} style={{ width: "100%", justifyContent: "center", marginTop: 4 }}>
            {creating
              ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Sending...</>
              : <><Icon name="send" size={14} color="#fff" /> Send to {selected.length} contact{selected.length !== 1 ? "s" : ""}</>}
          </Btn>
        </div>

        {/* Contact selector */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Select Contacts ({selected.length}/{chats.length})</div>
            <button onClick={() => setSelected(selected.length === chats.length ? [] : chats.map(c => c.contact))}
              style={{ fontSize: 12, color: T.emerald, background: "none", border: "none", cursor: "pointer", fontFamily: base.font, fontWeight: 600 }}>
              {selected.length === chats.length ? "Deselect all" : "Select all"}
            </button>
          </div>

          {/* Search in contacts */}
          <div style={{ position: "relative", marginBottom: 10 }}>
            <svg style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter contacts..."
              style={{ width: "100%", padding: "7px 12px 7px 28px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 8, color: T.text1, fontSize: 12, outline: "none", boxSizing: "border-box" }}
              onFocus={e => (e.target.style.borderColor = T.emerald)}
              onBlur={e  => (e.target.style.borderColor = T.border1)} />
          </div>

          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {!filteredChats.length && <div style={{ color: T.text3, fontSize: 13, padding: "16px 0", textAlign: "center" }}>No contacts found</div>}
            {filteredChats.map(c => {
              const name  = c.contactName || cleanPhone(c.contact);
              const isSelected = selected.includes(c.contact);
              return (
                <div key={c.contact} onClick={() => toggleContact(c.contact)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 4px", borderBottom: `1px solid ${T.border1}`, cursor: "pointer", borderRadius: 6, transition: "background 0.1s" }}>
                  <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${isSelected ? T.emerald : T.border2}`, background: isSelected ? T.emerald : "transparent", transition: "all 0.15s" }}>
                    {isSelected && <Icon name="check" size={10} color="#fff" />}
                  </div>
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: isSelected ? T.emeraldDim : "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: isSelected ? T.emerald : T.text2 }}>
                    {name[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                    <div style={{ fontSize: 11, color: T.text3, fontFamily: base.mono }}>{cleanPhone(c.contact)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );

  // ── Campaign list ─────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "clamp(14px, 4vw, 26px) clamp(14px, 4vw, 28px) 40px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text1 }}>Broadcast Campaigns</div>
          <div style={{ fontSize: 13, color: T.text3, marginTop: 2 }}>Send bulk messages to multiple contacts at once</div>
        </div>
        <Btn variant="primary" onClick={() => setView("new")}>
          <Icon name="plus" size={14} color="#fff" /> New campaign
        </Btn>
      </div>

      {/* Stats row */}
      {broadcasts.length > 0 && (
        <div className="grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Total",     val: broadcasts.length,                                     color: T.sky    },
            { label: "Completed", val: broadcasts.filter(b => b.status === "completed").length, color: T.emerald},
            { label: "Running",   val: broadcasts.filter(b => b.status === "running").length,   color: T.amber  },
            { label: "Msgs Sent", val: broadcasts.reduce((s, b) => s + (b.sentCount || 0), 0), color: T.violet },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ padding: "14px 16px", borderRadius: 12, background: T.bg2, border: `1px solid ${T.border1}` }}>
              <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: base.mono, lineHeight: 1 }}>{val}</div>
              <div style={{ fontSize: 11, color: T.text3, marginTop: 5 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {!broadcasts.length && (
        <Card style={{ textAlign: "center", padding: "48px 24px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14, opacity: 0.2 }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.22C1.6 2.1 2.43 1.18 3.55 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/></svg></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text2, marginBottom: 6 }}>No broadcasts yet</div>
          <div style={{ fontSize: 13, color: T.text3, marginBottom: 20 }}>Create your first campaign to send messages to multiple contacts</div>
          <Btn variant="primary" onClick={() => setView("new")}>Create first broadcast</Btn>
        </Card>
      )}

      {broadcasts.map(b => {
        const prog = progress[b._id];
        const sc   = { completed: "emerald", running: "amber", failed: "rose", draft: "sky" };
        return (
          <Card key={b._id} className="card-hover" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</div>
                  <Badge label={b.status.toUpperCase()} color={sc[b.status] || "sky"} />
                </div>
                <div style={{ fontSize: 13, color: T.text3, marginBottom: 12, lineHeight: 1.55, maxWidth: 500 }}>{b.message}</div>
                <div style={{ display: "flex", gap: 16, fontSize: 12, flexWrap: "wrap" }}>
                  <span style={{ color: T.text3 }}>{b.recipients?.length || 0} recipients</span>
                  <span style={{ color: T.emerald, fontWeight: 600 }}>{b.sentCount || 0} sent</span>
                  {(b.failedCount || 0) > 0 && <span style={{ color: T.rose, fontWeight: 600 }}>{b.failedCount} failed</span>}
                  <span style={{ color: T.text3 }}>{timeAgo(b.createdAt)}</span>
                </div>
              </div>
            </div>
            {prog && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.text3, marginBottom: 6 }}>
                  <span>Sending {prog.sent}/{prog.total} messages...</span>
                  <span style={{ fontWeight: 700, color: T.emerald, fontFamily: base.mono }}>{prog.progress}%</span>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: T.border1, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${prog.progress}%`, background: `linear-gradient(90deg,${T.emerald},${T.sky})`, transition: "width 0.4s", borderRadius: 3 }} />
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}